import { useEffect, type Dispatch, type SetStateAction } from "react";
import { useIntl } from "react-intl";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { Button } from "src/design-system/components/Button/Button";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { tw } from "src/design-system/tw";
import type { RoomDetail } from "../../HotelsContext";
import { DEFAULT_ROOM } from "../constants";
import { useHotelRoomValidation } from "../../../../utils/hooks/useHotelRoomValidation";
import { RoomDetails } from "./RoomDetails";
import messages from "./RoomDetails.messages";

type RoomDetailsFormProps = {
  freshFormData: RoomDetail[];
  setFreshFormData: Dispatch<SetStateAction<RoomDetail[]>>;
  onCtaClick: () => void;
  ctaMessage: string;
  setDisableMobileCta?: Dispatch<SetStateAction<boolean>>;
};

export function RoomsForm({
  freshFormData,
  setFreshFormData,
  onCtaClick,
  ctaMessage,
  setDisableMobileCta,
}: RoomDetailsFormProps) {
  const intl = useIntl();
  const { isMobile } = useScreenMinWidth();
  const isMoreThanOneRoom = freshFormData.length > 1;

  const { bookingWarnings, hasErrors, shouldDisableAddRoom } =
    useHotelRoomValidation({
      freshFormData,
      intl,
    });

  useEffect(() => {
    // Disable mobile CTA if there are any errors
    if (setDisableMobileCta) {
      setDisableMobileCta(hasErrors);
    }
  }, [hasErrors, setDisableMobileCta]);

  const handleAddRoom = () => {
    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:AddRoom",
    });
    const updatedDetails = [...freshFormData, DEFAULT_ROOM];
    setFreshFormData(updatedDetails);
  };

  const handleRemoveLastRoom = () => {
    if (freshFormData.length === 1) {
      sendAnalyticsInteractionEvent({
        category: "Hotels",
        action: "Click:ResetRoom",
      });
      setFreshFormData([DEFAULT_ROOM]);
    }
    if (isMoreThanOneRoom) {
      sendAnalyticsInteractionEvent({
        category: "Hotels",
        action: "Click:RemoveRoom",
      });
      const updatedRooms = [...freshFormData].slice(0, -1);
      setFreshFormData(updatedRooms);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <ul
        aria-label={intl.formatMessage(messages.roomsListLabel)}
        className="divide-y-1 divide-border-secondary"
      >
        {freshFormData.map((_, index) => (
          <RoomDetails
            key={index}
            roomIndex={index}
            freshFormData={freshFormData}
            setFreshFormData={setFreshFormData}
            maxGuestLimitReached={bookingWarnings.totalGuestLimitWarning.active}
            maxAdultsLimitReached={
              bookingWarnings.adultGuestLimitWarning.active
            }
            maxChildrenLimitReached={
              bookingWarnings.childrenGuestLimitWarning.active
            }
          />
        ))}
      </ul>

      <div className="flex w-full justify-between">
        <Button
          onPress={handleRemoveLastRoom}
          variant="subtle"
          className="text-text-secondary"
        >
          {isMoreThanOneRoom
            ? intl.formatMessage(messages.removeRoom)
            : intl.formatMessage(messages.reset)}
        </Button>
        <Button
          onPress={handleAddRoom}
          variant="subtle"
          className="text-text-secondary"
          isDisabled={shouldDisableAddRoom}
        >
          {intl.formatMessage(messages.addAnotherRoom)}
        </Button>
      </div>

      {/* Placeholder for mobile spacing so adding a child doesn't jump button up*/}
      {isMobile && !hasErrors && <div className="h-27" />}

      {Object.entries(bookingWarnings).map(([key, warning]) =>
        warning.active ? (
          <span
            key={key}
            className={tw(
              "rounded-xl p-4",
              warning.type === "error"
                ? "bg-surface-error"
                : "bg-surface-caution",
            )}
          >
            {warning.message}
          </span>
        ) : null,
      )}

      {!isMobile && (
        <Button
          size="large"
          onPress={onCtaClick}
          className="data-focus-visible:outline-2 data-focus-visible:outline-offset-2 data-focus-visible:outline-ring"
          isDisabled={hasErrors}
        >
          {ctaMessage}
        </Button>
      )}
    </div>
  );
}
